// ── Session F9 post-session email section (log + role-gated composer) ───────
// F9: every workspace role that can open session detail may read the per-session
// post-session email log. The composer (weekly assignment free text + trigger)
// is write-gated via sessions:send-invites so company_admin / cohort_lead remain
// read-only. Empty log is honest ("No post-session email has been recorded"),
// never a fabricated send history.

import { Alert, Button, FormField, Module, ModuleBody, ModuleHead, SkeletonRows, Textarea } from "@vjal/ui";
import { useState } from "react";

import { evaluateActionCapability } from "../../../domain/capabilities";
import type { CoachingRole } from "../../../domain/roles";
import { useCoachingPostSessionEmailLog } from "../../../hooks/useCoachingZ6Data";
import { useCoachingZ6Mutations } from "../../../hooks/useCoachingZ6Mutations";

export function SessionPostSessionEmailSection({
  sessionId,
  role
}: {
  sessionId: string;
  role: CoachingRole;
}) {
  const log = useCoachingPostSessionEmailLog(sessionId);
  const { postSessionEmail } = useCoachingZ6Mutations();
  const [assignmentText, setAssignmentText] = useState("");
  const writeDecision = evaluateActionCapability(role, "sessions:send-invites");
  const canCompose = writeDecision.allowed;
  const requests = log.data?.emailLog?.requests ?? [];
  const emailResult = postSessionEmail.data?.postSessionEmail;

  return (
    <Module span={12}>
      <ModuleHead
        title="Post-session email"
        subtitle="Standardized weekly email to participants. Delivery stays gated until the email provider opens."
      />
      <ModuleBody className="space-y-4">
        {canCompose ? (
          <div className="space-y-3">
            <Alert variant="info">
              Dynamic fields (participant name, week, recording link, passcode presence, coach name) are merged at
              send time. Add the weekly assignment below.
            </Alert>
            <FormField label="Weekly assignment (free text)" hint="Included in the email body to all participants.">
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={3}
                  value={assignmentText}
                  onChange={(event) => setAssignmentText(event.target.value)}
                  placeholder="Describe this week's assignment…"
                />
              )}
            </FormField>
            <Button
              type="button"
              variant="secondary"
              disabled={!sessionId || postSessionEmail.isPending}
              loading={postSessionEmail.isPending}
              onClick={() =>
                postSessionEmail.mutate({
                  sessionId,
                  body: { assignmentText: assignmentText.trim() || undefined }
                })
              }
            >
              Trigger post-session email
            </Button>
            {emailResult ? (
              <Alert variant={emailResult.providerMode === "live" ? "success" : "info"}>
                {`Queued for ${emailResult.recipientCount} recipient(s). Delivery mode: ${emailResult.providerMode}.`}
              </Alert>
            ) : null}
          </div>
        ) : null}

        {log.denied ? (
          <div data-honest-posture="permission-denied">
            <Alert variant="info">Your role can't view the post-session email log for this session.</Alert>
          </div>
        ) : log.isError ? (
          <div data-honest-posture="unavailable">
            <Alert variant="error">
              The post-session email log is temporarily unavailable
              {log.reference ? ` (reference: ${log.reference})` : ""}.
            </Alert>
          </div>
        ) : log.isLoading ? (
          <div data-honest-posture="loading" aria-label="Loading post-session email log">
            <SkeletonRows rows={2} columns={2} className="rounded-lg border border-line" />
          </div>
        ) : requests.length === 0 ? (
          <div data-honest-posture="unavailable">
            <Alert variant="info">No post-session email has been recorded for this session yet.</Alert>
          </div>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0" data-honest-posture-ready="post-session-email-log">
            {requests.map((request) => (
              <li key={request.requestId} className="flex items-center justify-between gap-3 text-sm text-ink-body">
                <span>
                  {request.templateVersion} · {request.providerMode === "live" ? "live" : "queued"}
                </span>
                <span className="text-ink-muted">
                  {request.sentCount} sent / {request.queuedCount} queued / {request.failedCount} failed
                </span>
              </li>
            ))}
          </ul>
        )}
      </ModuleBody>
    </Module>
  );
}
